import { Component, OnDestroy, OnInit } from '@angular/core';
import { takeUntil, flatMap, debounceTime, tap } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { Subject, BehaviorSubject } from 'rxjs';
import * as _ from 'lodash';
import { BriefingService, TenantService } from '../services';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormControl } from '@angular/forms';
import { Sort } from '@angular/material/sort';

interface SearchParams {
    page: number; 
    rows: number; 
    type?: string,
    searchTerm?: string; 
    sort?: string;
    sortDirection: string;
};

@Component({
    selector: 'briefings',
    styles: [`
        .scroll-container {
            max-height: calc(100vh - 132px);
            overflow-y: auto;
        }

        .cdk-column-title.mat-column-title {
            width: calc(100vw - 875px);
            max-width: calc(100vw - 875px);
            min-width: calc(100vw - 875px);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
        .cdk-column-schedule.mat-column-schedule {
            width: 250px;
            max-width: 250px;
            min-width: 250px;
        }
        .cdk-column-lastSent.mat-column-lastSent {
            width: 125px;
            max-width: 125px;
            min-width: 125px;
        }
        .cdk-column-nextSend.mat-column-nextSend {
            width: 125px;
            max-width: 125px;
            min-width: 125px;
        }
        .cdk-column-channel.mat-column-channel {
            width: 50px;
            max-width: 50px;
            min-width: 50px;
        }
        .cdk-column-actions.mat-column-actions {
            width: 30px;
            max-width: 30px;
            min-width: 30px;
        }
        .cdk-column-title.mat-column-title.mat-header-cell:first-of-type, 
        .cdk-column-title.mat-column-title.mat-cell:first-of-type, 
        .cdk-column-title.mat-column-title.mat-footer-cell:first-of-type {
            padding-left: 10px;
        }
        .cdk-column-actions.mat-column-actions.mat-header-cell:last-of-type, 
        .cdk-column-actions.mat-column-actions.mat-cell:last-of-type, 
        .cdk-column-actions.mat-column-actions.mat-footer-cell:last-of-type {
            padding-right: 10px;
        }
    `],
    template: `
        <div fxLayout="column">
            <mat-toolbar>
                <h3>Manage Briefings</h3>
                <span fxFlex></span>
                <mat-form-field style="font-size: 16px; font-weight: 400;">
                    <input matInput placeholder="Search Term" [formControl]="searchControl">
                </mat-form-field>
                <button *ngIf="createSlackEnabled && createEmailEnabled" mat-button color="primary" [matMenuTriggerFor]="createMenu">Add Briefing</button>
                <mat-menu #createMenu="matMenu">
                    <button mat-menu-item [routerLink]="['./create', 'SLACK']">Slack</button>
                    <button mat-menu-item [routerLink]="['./create', 'EMAIL']">Email</button>
                </mat-menu>
                <button *ngIf="createSlackEnabled && !createEmailEnabled" mat-button color="primary" [routerLink]="['./create', 'SLACK']">Add Briefing</button>
                <button *ngIf="!createSlackEnabled && createEmailEnabled" mat-button color="primary" [routerLink]="['./create', 'EMAIL']">Add Briefing</button>
            </mat-toolbar>
            <div *ngIf="noTouching" fxFlex fxLayoutAlign="center center"><spinner></spinner></div>
            <div *ngIf="!emptySearch"
                infiniteScroll
                [infiniteScrollThrottle]="300"
                [infiniteScrollDistance]="1"
                [scrollWindow]="false"
                (scrolled)="nextPage()"
                class="scroll-container">
                <table mat-table
                    matSort
                    (matSortChange)="sort($event)"
                    [matSortDisableClear]="true"
                    [matSortActive]="searchParams?.value?.sort"
                    [matSortDirection]="searchParams?.value?.sortDirection"
                    *ngIf="!noTouching"
                    [dataSource]="briefings" fxFlex="100"
                    style="width: 100%;">
                    <ng-container matColumnDef="title">
                        <th mat-header-cell *matHeaderCellDef mat-sort-header="titleLower">Title</th>
                        <td mat-cell *matCellDef="let row" >{{row.briefingTitle}}</td>
                    </ng-container>
                    <ng-container matColumnDef="schedule">
                        <th mat-header-cell *matHeaderCellDef>Schedule</th>
                        <td mat-cell *matCellDef="let row" >{{scheduleDescription(row.schedule)}}</td>
                    </ng-container>
                    <ng-container matColumnDef="lastSent">
                        <th mat-header-cell *matHeaderCellDef mat-sort-header="lastSentDate" start="desc">Last Sent</th>
                        <td mat-cell *matCellDef="let row" >{{row.lastSentDate | date:'short'}}</td>
                    </ng-container>
                    <ng-container matColumnDef="nextSend">
                        <th mat-header-cell *matHeaderCellDef mat-sort-header="schedule.nextScheduledDate" start="desc">Next Send</th>
                        <td mat-cell *matCellDef="let row" >
                            <span *ngIf="row.schedule?.nextScheduledDate">
                                {{row.schedule.nextScheduledDate | date:'short'}}
                            </span>
                            <button *ngIf="!row.schedule?.nextScheduledDate" mat-button (click)="sendNow(row)"><span>Send Now</span></button>
                        </td>
                    </ng-container>
                    <ng-container matColumnDef="channel">
                        <th mat-header-cell *matHeaderCellDef  mat-sort-header="type">Type</th>
                        <td mat-cell *matCellDef="let row" >{{typeNameFor(row)}}</td>
                    </ng-container>
                    <ng-container matColumnDef="actions">
                        <th mat-header-cell *matHeaderCellDef></th>
                        <td mat-cell *matCellDef="let row">
                            <mat-menu #menu="matMenu">
                                <a mat-menu-item [routerLink]="['./', row.id]">
                                    <mat-icon>build</mat-icon>
                                    <span>Edit</span>
                                </a>
                                <a mat-menu-item (click)="sendNow(row)">
                                    <mat-icon>send</mat-icon>
                                    <span>Send Now</span>
                                </a>
                                <a mat-menu-item (click)="deleteBriefing(row)">
                                    <mat-icon>clear</mat-icon>
                                    <span>Delete</span>
                                </a>
                            </mat-menu>

                            <button mat-icon-button class="edit-button" mat-icon-button [matMenuTriggerFor]="menu">
                                <mat-icon>settings</mat-icon>
                            </button>
                        </td>
                    </ng-container>
                    <tr mat-header-row *matHeaderRowDef="['title', 'schedule', 'lastSent', 'nextSend', 'channel', 'actions']"></tr>
                    <tr mat-row *matRowDef="let row; columns: ['title', 'schedule', 'lastSent', 'nextSend', 'channel', 'actions'];"></tr>
                </table>
            </div>
            <div *ngIf="emptySearch" fxLayoutAlign="center center">
            <h3>No briefings match your search criteria. <button mat-button (click)="clearSearch()">Clear search.</button></h3>
        </div>
        </div>
    `
})
export class ManageBriefingsListPaneComponent implements OnInit, OnDestroy {
    noTouching: boolean;
    private cleanupTime = new Subject<void>();
    searchControl = new FormControl();
    briefings = new BehaviorSubject<any[]>([]);
    searchParams = new BehaviorSubject<SearchParams>({ 
        page: 0,
        rows: 50,
        sort: 'titleLower',
        sortDirection: 'ASC'
    });
    lastFetched: boolean;
    active: boolean;
    slackChannels = {};
    createSlackEnabled: boolean;
    createEmailEnabled: boolean;
    emptySearch: boolean;

    constructor(private route: ActivatedRoute, 
                private snackbar: MatSnackBar,  
                private briefingService: BriefingService,
                private tenantService: TenantService) {}
    
    ngOnInit(): void {
        this.tenantService.getCurrentTenant()
        .subscribe(tenant => {
            const allowedBriefingTypes = tenant.settings.allowedBriefingTypes;
            this.createSlackEnabled = allowedBriefingTypes && allowedBriefingTypes.includes('SLACK');
            this.createEmailEnabled = allowedBriefingTypes && allowedBriefingTypes.includes('SENDGRID');
        });

        this.route.queryParams.pipe(takeUntil(this.cleanupTime)).subscribe(queryParams => {
            this.updateParams({ sort: queryParams.sort || undefined, sortDirection: queryParams.sortDirection || 'ASC' });
        })

        this.searchParams
        .pipe(
            flatMap((params: SearchParams) => this.briefingService.getBriefings(params)),
            tap(sources => this.lastFetched = sources.last),
            tap(response => this.emptySearch = response.first && response.last && !response.numberOfElements),
            takeUntil(this.cleanupTime)
        )
        .subscribe(results => this.briefings.next(this.briefings.value.concat(results.content)));

        this.searchControl.valueChanges
        .pipe(
            debounceTime(500),
            tap(() => this.briefings.next([])), 
            takeUntil(this.cleanupTime)
        )
        .subscribe(term => this.searchParams.next(Object.assign({}, this.searchParams.value, {term, page: 0})));
    }

    scheduleDescription = (schedule) => this.briefingService.scheduleDescription(schedule);

    updateParams(newData) {
        this.searchParams.next(Object.assign({}, newData, _.cloneDeep(this.searchParams.value)));
    }

    typeNameFor(row): string {
        if(!row.type || row.type === 'SENDGRID') {
            return 'Email';
        } else if(row.type === 'SLACK') {
            return 'Slack';
        } else if(row.type === 'TEAMS') {
            return 'Teams';
        } else {
            return '';
        }
    }

    sendNow(briefing): void {
        this.briefingService.sendNow(briefing.id)
        .subscribe(() => this.snackbar.open(`${briefing.briefingTitle} sent.`, 'OK', { duration: 3000}))
    }

    deleteBriefing(briefing): void {
        this.briefingService.confirmedDeleteBriefing(briefing)
        .subscribe(
            () => {
                this.briefings.next([]);
                this.searchParams.next(Object.assign({},this.searchParams.value, { page: 0 }));
            },
            () => this.snackbar.open(`The briefing ${briefing.briefingTitle} couldn't be deleted.`, 'OK', { duration: 3000})
        );
    }

    clearSearch() {
        this.searchControl.setValue('');
        this.searchControl.updateValueAndValidity();
        this.emptySearch = false;
    }

    nextPage() {
        if (!this.lastFetched) {
            this.updateParams({ page: this.searchParams.value.page++ });
        }
    }

    sort(sort: Sort): void {
        const params = this.searchParams.value;
        params.page = 0;
        params.sort = sort.active;
        params.sortDirection = sort.direction;
        this.briefings.next([]);
        this.searchParams.next(params);
    }

    ngOnDestroy(): void {
        this.cleanupTime.next();
        this.cleanupTime.complete();
    }
}